import React, { useState, forwardRef, useImperativeHandle, useRef, useCallback } from 'react';
import { Table, TableContainer, Paper, Box, Typography } from '@mui/material';
import { EditableTableProps, BaseTableRow } from './types';
import { TableHead } from './tables/TableHead';
import { TableBody } from './tables/TableBody';
import { useWarningModal, WarningModal } from './WarningModal';
import { useEditableTableContext } from './Context/EditableTableContext';

// Define the ref handle type
export interface EditableTableRef {
  addNewRow: () => void;
}

// Internal component with proper typing
function EditableTableInner<T extends BaseTableRow>(
  {
    headers,
    data,
    onAction,
    EditableComponent,
    onScroll,
    onBeforeDelete,
    modalOpen,
    activeModalContent,
    inactiveModalContent,
    type,
  }: EditableTableProps<T>,
  ref: React.Ref<EditableTableRef>
): React.ReactElement {
  console.log({ EditableComponent });
  const { editingRowId, setEditingRowId, isAddingNewRow, setIsAddingNewRow } = useEditableTableContext();
  const [localData, setLocalData] = useState<T[]>(data);
  const { handleOpen: setWarningOpen, ...modalprops } = useWarningModal();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const scrollStep = 48;
  const handleTableKeyDown = useCallback((e: React.KeyboardEvent) => {
    const el = tableContainerRef.current;
    if (!el) return;
    const target = e.target as HTMLElement | null;
    const tagName = target?.tagName?.toLowerCase();
    const isTypingTarget =
      tagName === 'input' ||
      tagName === 'textarea' ||
      target?.isContentEditable === true ||
      target?.getAttribute('role') === 'textbox' ||
      target?.getAttribute('role') === 'combobox';
    // Do not hijack keyboard while user is typing in row inputs.
    if (isTypingTarget) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        el.scrollBy({ top: scrollStep, behavior: 'smooth' });
        break;
      case 'ArrowUp':
        e.preventDefault();
        el.scrollBy({ top: -scrollStep, behavior: 'smooth' });
        break;
      case 'PageDown':
        e.preventDefault();
        el.scrollBy({ top: el.clientHeight, behavior: 'smooth' });
        break;
      case 'PageUp':
        e.preventDefault();
        el.scrollBy({ top: -el.clientHeight, behavior: 'smooth' });
        break;
      case 'Home':
        e.preventDefault();
        el.scrollTo({ top: 0, behavior: 'smooth' });
        break;
      case 'End':
        e.preventDefault();
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        break;
      case ' ':
        e.preventDefault();
        el.scrollBy({ top: el.clientHeight * 0.8, behavior: 'smooth' });
        break;
      default:
        break;
    }
  }, []);

  React.useEffect(() => {
    setLocalData(data);
  }, [data]);

  // Auto-focus table when modal opens so keyboard scroll works without clicking first
  React.useEffect(() => {
    if (modalOpen !== true) return;
    const timer = setTimeout(() => {
      tableContainerRef.current?.focus({ preventScroll: true });
    }, 100);
    return () => clearTimeout(timer);
  }, [modalOpen]);

  const handleEdit = (row: T, index: number) => {
    if (row.isEditable) {
      setEditingRowId(row.id || index);
      onAction('onedit', row, index);
    }
  };

  const handleDelete = (row: T, index: number) => {
    if (row.isDeletable) {
      // Check if onBeforeDelete callback exists and returns false
      // If it returns false, don't show the confirmation modal
      if (onBeforeDelete && !onBeforeDelete(row, index)) {
        return; // Prevent modal from showing
      }

      setWarningOpen({
        onContinue: () => {
          onAction('ondelete', row, index);
        },
        heading:` Are you sure you want to Delete this ${type}?`,
        word: 'Delete',
        description: `This ${type} will be deleted and will not be available for new stock transactions.`,
        type,
      });
    }
  };

  const handleCancel = (row: T, index: number) => {
    setEditingRowId(null);
    setIsAddingNewRow(false);
    onAction('oncancel', row, index);
  };

  const handleAddNewRow = () => {
    console.log('handleAddNewRow called inside EditableTable');
    console.log('Current state - isAddingNewRow:', isAddingNewRow, 'editingRowId:', editingRowId);
    setIsAddingNewRow(true);
    const tempId = `new-${Date.now()}`;
    setEditingRowId(tempId);
    console.log('Set isAddingNewRow to true and editingRowId to:', tempId);
  };

  useImperativeHandle(ref, () => {
    console.log('useImperativeHandle - creating ref handle');
    return {
      addNewRow: handleAddNewRow,
    };
  });

  const handleSaveNewRow = (newRow: T) => {
    const rowToAdd = { ...newRow, id: newRow.id || Date.now() };
    setLocalData((prev) => [...prev, rowToAdd]);
    setIsAddingNewRow(false);
    setEditingRowId(null);
    onAction('onsave', rowToAdd, localData.length);
  };

  const handleActive = (row: T, index: number) => {
    setWarningOpen({
      onContinue: () => {
        onAction('onactive', row, index);
      },
      heading:` Are you sure you want to Active this ${type}?`,
      word: 'Active',
      description: `This ${type} will be available for new stock transactions.`,
      type,
    });
  };

  const handleInactive = (row: T, index: number) => {
    setWarningOpen({
      onContinue: () => {
        onAction('oninactive', row, index);
      },
      heading:` Are you sure you want to Inactive this ${type}?`,
      word: 'Inactive',
      description:  `This ${type} will not be available for new stock transactions.`,
      type,
    });
  };

  const handleSave = (updatedRow: T) => {
    const index = localData.findIndex((r) => {
      const rowId = r.id !== undefined ? r.id : localData.indexOf(r);
      const editingId = editingRowId !== null ? editingRowId : -1;
      return rowId === editingId;
    });

    if (index !== -1) {
      const newData = [...localData];
      newData[index] = { ...newData[index], ...updatedRow, isEditing: false };
      setLocalData(newData);
      setEditingRowId(null);
      onAction('onsave', updatedRow, index);
    }
  };

  const newRowTemplate: T = {
    isEditable: true,
    isDeletable: true,
    isActive: true,
  } as T;

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <TableContainer
          ref={tableContainerRef}
          component={Paper}
          onScroll={onScroll}
          onKeyDown={handleTableKeyDown}
          tabIndex={0}
          sx={{
            flex: 1,
            overflow: 'auto',
            boxShadow: 'none',
            border: 'none',
            '& .MuiPaper-root': { border: 'none' },
            '&:focus': { outline: '1px solid', outlineColor: 'divider' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              border: 'none',
              '& .MuiTableCell-head': {
                borderBottom: 'none !important',
              },
              '& .MuiTableRow-root:last-child td': {
                borderBottom: 'none !important',
              },
            }}
          >
            <TableHead<T> headers={headers} />
            <TableBody<T>
              headers={headers}
              data={localData}
              editingRowId={editingRowId}
              isAddingNewRow={isAddingNewRow}
              newRowTemplate={isAddingNewRow ? (newRowTemplate as T) : undefined}
              EditableComponent={EditableComponent}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onActive={handleActive}
              onInactive={handleInactive}
              onSave={handleSave}
              onSaveNewRow={handleSaveNewRow}
              onCancel={handleCancel}
            />
          </Table>
        </TableContainer>
      </Box>
      {modalprops.open && <WarningModal {...modalprops} />}
    </>
  );
}

function withRef<T extends BaseTableRow>(
  component: (props: EditableTableProps<T>, ref: React.Ref<EditableTableRef>) => React.ReactElement
) {
  return forwardRef(component) as unknown as (props: EditableTableProps<T> & { ref?: React.Ref<EditableTableRef> }) => React.ReactElement;
}
export const EditableTable = withRef(EditableTableInner);
